import { useId, useMemo, useState } from 'react';
import { ArrowRight, Search, Building2 } from 'lucide-react';
import ClassCard from '../ClassCard';
import SectionHeading from '../SectionHeading';
import { getAllProvinces, getDistricts } from '../../data/regions';

export interface CategoryClassItem {
  id: string;
  title: string;
  provider_id: string;
  provider_name: string;
  thumbnail_url: string | null;
  price: number;
  discount_price: number;
  start_date: string;
  end_date: string;
  location_type: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  location_address: string | null;
  location_province?: string | null;
  location_district?: string | null;
  class_type: 'CERTIFICATE' | 'COMPLETION' | 'WORKSHOP' | 'OTHER';
  status: 'SCHEDULED' | 'RECRUITING' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED';
  capacity: number;
  current_count: number;
  closing_soon: boolean;
  favorites_count?: number;
  is_premium?: boolean;
  category_path?: { id: string; name: string; level: number }[];
  instructors?: {
    id: string;
    name: string;
    description: string;
    image_url: string | null;
  }[];
}

export interface CategoryNode {
  id: string;
  parent_id: string | null;
  name: string;
  level: 1 | 2 | 3;
  sort_order: number;
}

interface ClassCategoryViewProps {
  title: string;
  classes: CategoryClassItem[];
  /** 전체 카테고리 트리(class_categories). 수업 등록 여부와 무관하게 좌측 필터에 1·2차 전부 노출하기 위해 사용. */
  categoryTree?: CategoryNode[];
  premiumProviderIds: Set<string>;
  onInstructorClick?: (instructorId: string) => void;
  loading?: boolean;
  emptyMessage?: string;
  /** 로그인한 사용자. provider/admin일 때만 "내 기관 보기" 토글이 노출됨. */
  currentUser?: { id: string; role: string } | null;
}

function getLocationText(cls: CategoryClassItem): string {
  if (cls.location_type === 'ONLINE') return '온라인';
  if (cls.location_type === 'OFFLINE') return cls.location_address || '-';
  return '온/오프라인';
}

/** ClassItem → ClassCard 매핑 (인기 수업/전체 목록 공용) */
export function renderClassCard(
  cls: CategoryClassItem,
  premiumProviderIds: Set<string>,
  onInstructorClick?: (instructorId: string) => void,
  rank?: number,
) {
  return (
    <ClassCard
      key={cls.id}
      id={cls.id}
      rank={rank}
      title={cls.title}
      providerName={cls.provider_name}
      thumbnailUrl={cls.thumbnail_url || ''}
      price={cls.price}
      discountPrice={cls.discount_price}
      startDate={cls.start_date}
      endDate={cls.end_date}
      location={getLocationText(cls)}
      locationType={cls.location_type}
      classType={cls.class_type}
      status={cls.status}
      capacity={cls.capacity}
      currentCount={cls.current_count}
      closingSoon={cls.closing_soon}
      isPremium={cls.is_premium || premiumProviderIds.has(cls.provider_id)}
      categoryPath={cls.category_path}
      instructors={cls.instructors}
      onInstructorClick={onInstructorClick}
    />
  );
}

/** 인기 수업 카드 그리드 (좋아요 > 0 기준, 최대 maxItems개) */
export function PopularClassGrid({
  classes,
  premiumProviderIds,
  onInstructorClick,
  maxItems = 8,
  eyebrow = '지금, 더 나은 나를 위한 배움',
  title = '접수 중인 인기 수업을 만나보세요',
  onViewAll,
  gridColsClass = 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
}: {
  classes: CategoryClassItem[];
  premiumProviderIds: Set<string>;
  onInstructorClick?: (instructorId: string) => void;
  maxItems?: number;
  eyebrow?: string;
  title?: string;
  /** 우측 "전체 과정 보기" — 없으면 링크를 숨긴다 */
  onViewAll?: () => void;
  gridColsClass?: string;
}) {
  const popular = useMemo(() => {
    return [...classes]
      .filter((c) => (c.favorites_count || 0) > 0 && c.status === 'RECRUITING')
      .sort((a, b) => (b.favorites_count || 0) - (a.favorites_count || 0))
      .slice(0, maxItems);
  }, [classes, maxItems]);

  return (
    <section>
      <SectionHeading
        eyebrow={eyebrow}
        title={title}
        titleWeight="extrabold"
        right={
          onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="inline-flex items-center gap-1.5 text-[0.82rem] font-bold text-gray-700 hover:text-accent transition-colors shrink-0"
            >
              전체 과정 보기
              <ArrowRight className="w-4 h-4" />
            </button>
          )
        }
      />
      {popular.length === 0 ? (
        <p className="text-center text-gray-400 py-8">현재 모집중인 인기 수업이 없습니다</p>
      ) : (
        <div className={`grid ${gridColsClass} gap-4`}>
          {popular.map((cls, i) => renderClassCard(cls, premiumProviderIds, onInstructorClick, i + 1))}
        </div>
      )}
    </section>
  );
}

interface CatOption {
  id: string;
  name: string;
}

/**
 * 이 탭 수업들의 category_path에서 특정 레벨의 카테고리 옵션을 중복 제거하여 추출.
 * parentIds가 주어지면 그 부모들(한 단계 위 레벨, 다중) 중 하나에 속한 자식만 추린다. → 다중 선택 faceted 필터.
 */
function facetOptions(
  classes: CategoryClassItem[],
  level: 1 | 2 | 3,
  parentIds: string[] | null,
): CatOption[] {
  const map = new Map<string, string>();
  for (const c of classes) {
    const path = c.category_path || [];
    const node = path[level - 1];
    if (!node) continue;
    if (parentIds) {
      const parent = path[level - 2];
      if (!parent || !parentIds.includes(parent.id)) continue;
    }
    if (!map.has(node.id)) map.set(node.id, node.name);
  }
  return Array.from(map, ([id, name]) => ({ id, name }));
}

const toggleId = (arr: string[], id: string) =>
  arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

/** 1차 그룹별 색상 팔레트 (그룹 순서대로 순환). 라벨 점·그룹명 색 + 2차 칩 활성/비활성 색. */
const GROUP_COLORS: { dot: string; label: string; chipActive: string; chipIdle: string }[] = [
  { dot: 'bg-emerald-500', label: 'text-emerald-700', chipActive: 'bg-emerald-500 text-white', chipIdle: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' },
  { dot: 'bg-blue-500', label: 'text-blue-700', chipActive: 'bg-blue-500 text-white', chipIdle: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
  { dot: 'bg-purple-500', label: 'text-purple-700', chipActive: 'bg-purple-500 text-white', chipIdle: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
  { dot: 'bg-amber-500', label: 'text-amber-700', chipActive: 'bg-amber-500 text-white', chipIdle: 'bg-amber-50 text-amber-600 hover:bg-amber-100' },
  { dot: 'bg-rose-500', label: 'text-rose-700', chipActive: 'bg-rose-500 text-white', chipIdle: 'bg-rose-50 text-rose-600 hover:bg-rose-100' },
  { dot: 'bg-cyan-500', label: 'text-cyan-700', chipActive: 'bg-cyan-500 text-white', chipIdle: 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100' },
  { dot: 'bg-indigo-500', label: 'text-indigo-700', chipActive: 'bg-indigo-500 text-white', chipIdle: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100' },
  { dot: 'bg-teal-500', label: 'text-teal-700', chipActive: 'bg-teal-500 text-white', chipIdle: 'bg-teal-50 text-teal-600 hover:bg-teal-100' },
];

/**
 * 카테고리 탭 공용 뷰: 좌측 필터(검색 + 1·2·3차 다중 선택) + 우측 콘텐츠(인기 수업 3열 + 전체 목록 4열).
 * 2차는 1차 선택 시, 3차는 2차 선택 시 노출. 레벨 내 다중 선택(OR), 레벨 간 AND. 옵션은 이 탭 수업의 category_path에서 파생.
 */
export default function ClassCategoryView({
  title,
  classes,
  categoryTree,
  premiumProviderIds,
  onInstructorClick,
  loading,
  emptyMessage = '등록된 수업이 없습니다.',
  currentUser,
}: ClassCategoryViewProps) {
  // '전체 과정 보기' 클릭 시 스크롤할 전체 목록 섹션 id (탭마다 고유)
  const allListId = `class-all-list-${useId()}`;
  const [search, setSearch] = useState('');
  const [selectedL2, setSelectedL2] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'active' | 'completed'>('active');
  const [onlyMyInstitution, setOnlyMyInstitution] = useState(false);
  const [provinceFilter, setProvinceFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');

  const canFilterMyInstitution = currentUser?.role === 'provider' || currentUser?.role === 'admin';

  // 좌측 필터 그룹: 모든 1차 + 각 1차에 속한 모든 2차 (등록된 수업 유무와 무관하게 전체 트리에서 파생).
  // 선택은 2차만 가능 — 1차는 그룹 라벨일 뿐. categoryTree 미제공 시 수업 category_path 기반으로 폴백.
  const l2Groups = useMemo<{ id: string; name: string; children: CatOption[] }[]>(() => {
    if (categoryTree && categoryTree.length > 0) {
      const l1s = categoryTree
        .filter((c) => c.level === 1)
        .sort((a, b) => a.sort_order - b.sort_order);
      return l1s.map((l1) => ({
        id: l1.id,
        name: l1.name,
        children: categoryTree
          .filter((c) => c.level === 2 && c.parent_id === l1.id)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((c) => ({ id: c.id, name: c.name })),
      }));
    }
    // 폴백: 트리 미제공 시 기존 동작(등록된 수업의 2차만, 그룹 없이)
    const opts = facetOptions(classes, 2, null);
    return opts.length > 0 ? [{ id: '__all__', name: '', children: opts }] : [];
  }, [categoryTree, classes]);

  const hasAnyOption = useMemo(
    () => l2Groups.some((g) => g.children.length > 0),
    [l2Groups],
  );

  const hasFilter = selectedL2.length > 0;

  // 검색 + 카테고리 + 내 기관 + 지역 기본 필터 (상태 무관)
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return classes.filter((c) => {
      const path = c.category_path || [];
      if (selectedL2.length && !selectedL2.includes(path[1]?.id)) return false;
      if (
        q &&
        !c.title.toLowerCase().includes(q) &&
        !c.provider_name.toLowerCase().includes(q)
      )
        return false;
      if (onlyMyInstitution && currentUser && c.provider_id !== currentUser.id) return false;
      if (provinceFilter && c.location_province !== provinceFilter) return false;
      if (districtFilter && c.location_district !== districtFilter) return false;
      return true;
    });
  }, [classes, search, selectedL2, onlyMyInstitution, currentUser, provinceFilter, districtFilter]);

  // 인기 수업용: 모집중인 것만
  const activeFiltered = useMemo(
    () => baseFiltered.filter((c) => c.status === 'RECRUITING'),
    [baseFiltered],
  );

  // 전체 목록용: 상태 탭 기준 (진행중 = 모집중인 것만)
  const filtered = useMemo(() => {
    if (statusFilter === 'completed') return baseFiltered.filter((c) => c.status !== 'RECRUITING');
    return baseFiltered.filter((c) => c.status === 'RECRUITING');
  }, [baseFiltered, statusFilter]);

  const handleToggleL2 = (id: string) => setSelectedL2((prev) => toggleId(prev, id));
  const clearAll = () => setSelectedL2([]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      {/* 좌측 카테고리 필터 사이드바 */}
      <aside className="w-full lg:w-64 lg:shrink-0 lg:sticky lg:top-24 space-y-4">
        {/* 검색 */}
        <div className="bg-white rounded-2xl shadow-soft p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="수업명 검색, 교육기관 검색..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>

          {/* 지역 필터 (시/도, 시/군/구) */}
          <div className="grid grid-cols-2 gap-2">
            <select
              value={provinceFilter}
              onChange={(e) => {
                setProvinceFilter(e.target.value);
                setDistrictFilter('');
              }}
              className="px-2.5 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            >
              <option value="">전체 지역</option>
              {getAllProvinces().map((province) => (
                <option key={province} value={province}>{province}</option>
              ))}
            </select>
            <select
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              disabled={!provinceFilter}
              className="px-2.5 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <option value="">전체 시/군/구</option>
              {provinceFilter && getDistricts(provinceFilter).map((district) => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </div>

          {/* 내 기관 보기 (provider/admin 전용) */}
          {canFilterMyInstitution && (
            <button
              type="button"
              onClick={() => setOnlyMyInstitution((prev) => !prev)}
              className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                onlyMyInstitution
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Building2 className="w-4 h-4" />
              내 기관 보기
            </button>
          )}
        </div>

        {/* 카테고리 (1차 그룹별 + 2차 다중 선택). 1차는 라벨, 선택·필터는 2차만. */}
        {hasAnyOption && (
          <div className="bg-white rounded-2xl shadow-soft p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-gray-900">
                카테고리
                {selectedL2.length > 0 && (
                  <span className="ml-1.5 text-xs font-semibold text-primary">{selectedL2.length}</span>
                )}
              </span>
              {hasFilter && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-gray-400 hover:text-primary transition-colors"
                >
                  초기화
                </button>
              )}
            </div>
            <div className="divide-y divide-gray-100">
              {l2Groups.map((group, gi) => {
                const color = GROUP_COLORS[gi % GROUP_COLORS.length];
                return (
                  <div key={group.id} className="py-3 first:pt-0 last:pb-0">
                    {group.name && (
                      <div className={`flex items-center gap-1.5 mb-2 text-xs font-bold ${color.label}`}>
                        <span className={`w-2 h-2 rounded-full ${color.dot}`} />
                        {group.name}
                      </div>
                    )}
                    {group.children.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {group.children.map((opt) => {
                          const active = selectedL2.includes(opt.id);
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => handleToggleL2(opt.id)}
                              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                active ? color.chipActive : color.chipIdle
                              }`}
                            >
                              {opt.name}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-300">하위 카테고리 없음</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </aside>

      {/* 우측 콘텐츠 */}
      <div className="flex-1 min-w-0 space-y-8">
        {/* 접수 중인 인기 수업 (3열, 모집중인 것만) — 목업대로 배경 패널 없이 */}
        <PopularClassGrid
          classes={activeFiltered}
          premiumProviderIds={premiumProviderIds}
          onInstructorClick={onInstructorClick}
          onViewAll={() => document.getElementById(allListId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          gridColsClass="grid-cols-2 sm:grid-cols-3 lg:grid-cols-3"
        />

        {/* 전체 목록 (4열) — 목업 '전체 교육 과정' 패널 스타일 */}
        <section
          id={allListId}
          className="space-y-4 rounded-3xl border border-slate-200 p-5 sm:p-8 shadow-sm"
          style={{ background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%)' }}
        >
          <SectionHeading
            eyebrow="나에게 필요한 교육을 검색해보세요"
            title={`${title} 전체`}
            count={filtered.length}
          />
          {/* 진행 상태 탭 */}
          <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors border-b-2 -mb-[5px] ${
                statusFilter === 'active'
                  ? 'border-primary text-primary bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              진행중
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors border-b-2 -mb-[5px] ${
                statusFilter === 'completed'
                  ? 'border-gray-500 text-gray-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              종료
            </button>
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-soft">
              <p className="text-gray-500">
                {classes.length === 0 ? emptyMessage : '조건에 맞는 수업이 없습니다.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((cls) => renderClassCard(cls, premiumProviderIds, onInstructorClick))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
